import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  PersonAdd,
  Edit,
  Delete,
  ToggleOn,
  ToggleOff,
  RestoreFromTrash,
  DeleteForever
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import AppLayout from '../../components/layout/AppLayout';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import usersService from '../../services/usersService';
import { Role, type User } from '../../types/user.types';
import type { TableColumn } from '../../types/common.types';
import { getErrorMessage } from '../../utils/api';

const UsersListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null
  });
  const [hardDeleteDialog, setHardDeleteDialog] = useState<{ open: boolean; user: User | null }>({
    open: false,
    user: null
  });
  const [toggleDialog, setToggleDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({
    open: false,
    user: null
  });
  const [showDeleted, setShowDeleted] = useState(false);

  // Filters
  const [filters, setFilters] = useState<{
    search: string;
    ruolo: string;
    attivo: string;
  }>({
    search: '',
    ruolo: '',
    attivo: ''
  });

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch users
  const { data, isLoading } = useQuery({
    queryKey: ['users', page, rowsPerPage, debouncedSearch, filters.ruolo, filters.attivo, showDeleted],
    queryFn: () =>
      usersService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch,
        ruolo: filters.ruolo as Role | undefined,
        attivo: filters.attivo === '' ? undefined : filters.attivo === 'true',
        includeDeleted: showDeleted
      })
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteDialog({ open: false, id: null });
      enqueueSnackbar('Utente eliminato con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'eliminazione'), { variant: 'error' });
    }
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => usersService.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      const user = toggleDialog.user;
      const newStatus = user?.isActive ? 'disattivato' : 'attivato';
      setToggleDialog({ open: false, user: null });
      enqueueSnackbar(`Utente ${newStatus} con successo`, { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante la modifica dello stato'), { variant: 'error' });
    }
  });

  // Restore mutation
  const restoreMutation = useMutation({
    mutationFn: (id: string) => usersService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      enqueueSnackbar('Utente ripristinato con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il ripristino'), { variant: 'error' });
    }
  });

  // Hard delete mutation
  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => usersService.hardDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setHardDeleteDialog({ open: false, user: null });
      enqueueSnackbar('Utente eliminato definitivamente', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'eliminazione definitiva'), { variant: 'error' });
    }
  });

  // Stile per le righe eliminate (soft deleted)
  const getDeletedRowStyle = (row: User) => {
    if (row.isActive === false) {
      return {
        opacity: 0.6,
        fontStyle: 'italic',
        backgroundColor: 'action.hover',
        '& td': {
          color: 'text.disabled',
          fontStyle: 'italic'
        }
      };
    }
    return {};
  };

  const getDeletedCardStyle = (row: User) => {
    if (row.isActive === false) {
      return {
        opacity: 0.7,
        backgroundColor: 'action.disabledBackground',
        '& *': {
          fontStyle: 'italic'
        }
      };
    }
    return {};
  };

  const getRoleBadgeColor = (ruolo: Role): 'error' | 'primary' | 'default' => {
    switch (ruolo) {
      case Role.ADMIN:
        return 'error';
      case Role.OPERATORE:
        return 'primary';
      default:
        return 'default';
    }
  };

  const columns: TableColumn<User>[] = [
    {
      id: 'nome',
      label: 'Nome Completo',
      minWidth: 200,
      format: (_, row) => `${row.nome} ${row.cognome}`
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 200
    },
    {
      id: 'ruolo',
      label: 'Ruolo',
      minWidth: 130,
      format: (_, row) => (
        <Chip
          label={row.ruolo}
          color={getRoleBadgeColor(row.ruolo)}
          size="small"
        />
      )
    },
    {
      id: 'isActive',
      label: 'Stato',
      minWidth: 120,
      format: (_, row) => (
        <Chip
          label={row.isActive ? 'Attivo' : 'Non Attivo'}
          color={row.isActive ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ];

  const handleDelete = (id: string) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = () => {
    if (deleteDialog.id) {
      deleteMutation.mutate(deleteDialog.id);
    }
  };

  const handleToggleActive = (user: User) => {
    setToggleDialog({ open: true, user });
  };

  const confirmToggleActive = () => {
    if (toggleDialog.user) {
      toggleActiveMutation.mutate(toggleDialog.user.id);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleRestore = (id: string) => {
    restoreMutation.mutate(id);
  };

  const handleHardDelete = (user: User) => {
    setHardDeleteDialog({ open: true, user });
  };

  const confirmHardDelete = () => {
    if (hardDeleteDialog.user) {
      hardDeleteMutation.mutate(hardDeleteDialog.user.id);
    }
  };

  const renderActions = (row: User) => {
    // Se l'utente è eliminato (soft deleted), mostra azioni di ripristino/eliminazione definitiva
    if (!row.isActive) {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Ripristina">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleRestore(row.id)}
              disabled={restoreMutation.isPending}
            >
              <RestoreFromTrash fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Elimina definitivamente">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleHardDelete(row)}
              disabled={hardDeleteMutation.isPending}
            >
              <DeleteForever fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // Utente attivo: azioni normali
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Modifica">
          <IconButton size="small" onClick={() => navigate(`/users/${row.id}/edit`)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={row.isActive ? 'Disattiva' : 'Attiva'}>
          <IconButton
            size="small"
            color={row.isActive ? 'warning' : 'success'}
            onClick={() => handleToggleActive(row)}
            disabled={toggleActiveMutation.isPending}
          >
            {row.isActive ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Elimina">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row.id)}
            disabled={deleteMutation.isPending}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  if (isLoading && !data) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento utenti..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Gestione Utenti
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => navigate('/users/new')}
          >
            Nuovo Utente
          </Button>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Filtri
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showDeleted}
                  onChange={(e) => {
                    setShowDeleted(e.target.checked);
                    setPage(0);
                  }}
                  color="warning"
                />
              }
              label="Mostra eliminati"
            />
          </Box>
          <form onSubmit={(e) => e.preventDefault()}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <TextField
                label="Cerca"
                placeholder="Nome, cognome, email..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
                fullWidth
                helperText={filters.search !== debouncedSearch ? "Ricerca in corso..." : undefined}
              />
              <TextField
                select
                label="Ruolo"
                value={filters.ruolo}
                onChange={(e) => handleFilterChange('ruolo', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">Tutti</MenuItem>
                <MenuItem value={Role.ADMIN}>Admin</MenuItem>
                <MenuItem value={Role.OPERATORE}>Operatore</MenuItem>
              </TextField>
              <TextField
                select
                label="Stato"
                value={filters.attivo}
                onChange={(e) => handleFilterChange('attivo', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">Tutti</MenuItem>
                <MenuItem value="true">Attivo</MenuItem>
                <MenuItem value="false">Non Attivo</MenuItem>
              </TextField>
            </Box>
          </form>
        </Paper>

        <DataTable
          columns={columns}
          data={data?.data || []}
          total={data?.total || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          actions={renderActions}
          loading={isLoading}
          getRowSx={getDeletedRowStyle}
          getCardSx={getDeletedCardStyle}
          emptyStateComponent={
            <EmptyState
              title="Nessun utente trovato"
              message={
                debouncedSearch || filters.ruolo || filters.attivo
                  ? "Non ci sono utenti che corrispondono ai filtri selezionati. Prova a modificare i criteri di ricerca."
                  : "Non ci sono ancora utenti nel sistema. Crea il primo utente per iniziare a gestire il team."
              }
              actionLabel={!debouncedSearch && !filters.ruolo && !filters.attivo ? "Crea Primo Utente" : undefined}
              onAction={!debouncedSearch && !filters.ruolo && !filters.attivo ? () => navigate('/users/new') : undefined}
            />
          }
        />

        <ConfirmDialog
          open={deleteDialog.open}
          title="Conferma Eliminazione"
          message="Sei sicuro di voler eliminare questo utente? Questa azione non puo essere annullata."
          confirmText="Elimina"
          confirmColor="error"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteDialog({ open: false, id: null })}
          loading={deleteMutation.isPending}
        />

        <ConfirmDialog
          open={toggleDialog.open}
          title={`Conferma ${toggleDialog.user?.isActive ? 'Disattivazione' : 'Attivazione'}`}
          message={`Sei sicuro di voler ${toggleDialog.user?.isActive ? 'disattivare' : 'attivare'} l'utente ${toggleDialog.user?.nome} ${toggleDialog.user?.cognome}?`}
          confirmText={toggleDialog.user?.isActive ? 'Disattiva' : 'Attiva'}
          confirmColor={toggleDialog.user?.isActive ? 'warning' : 'success'}
          onConfirm={confirmToggleActive}
          onCancel={() => setToggleDialog({ open: false, user: null })}
          loading={toggleActiveMutation.isPending}
        />

        <ConfirmDialog
          open={hardDeleteDialog.open}
          title="Eliminazione Definitiva"
          message={`ATTENZIONE: Stai per eliminare definitivamente l'utente ${hardDeleteDialog.user?.nome} ${hardDeleteDialog.user?.cognome}. Questa azione è IRREVERSIBILE e tutti i dati dell'utente saranno persi permanentemente. Sei sicuro di voler procedere?`}
          confirmText="Elimina Definitivamente"
          confirmColor="error"
          onConfirm={confirmHardDelete}
          onCancel={() => setHardDeleteDialog({ open: false, user: null })}
          loading={hardDeleteMutation.isPending}
        />
      </Box>
    </AppLayout>
  );
};

export default UsersListPage;
